import { FC, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { FieldValues, useForm } from "react-hook-form";
import { OrganizationSearch } from "./OrganizationSearch";
import { OrganizationTable } from "./OrganizationTable";
import { useSetCompaniesState } from "../hooks/useSetCompaniesState";
import { useCompaniesValue } from "../hooks/useCompaniesValue";
import { fetchQueryCompanies } from "../miscellaneous/fetchQueryCompanies";
import { usePaginationTable } from "../../common/hooks/usePaginationTable";
import { useCompanyManagementSearchValue } from "../../company-management/hooks/useCompanyManagementSearchValue";
import { useSetCompanyManagementSearch } from "../../company-management/hooks/useSetCompanyManagementSearch";
import { defaultCompanyManagementSearchState } from "../../company-management/miscellaneous/companyManagementSearchAtom";

export const OrganizationManagement: FC = () => {
  const setCompanies = useSetCompaniesState();
  const companies = useCompaniesValue();
  const [totalRecords, setTotalRecords] = useState(0);

  const searchAtomState = useCompanyManagementSearchValue();
  const setSearchAtomState = useSetCompanyManagementSearch();
  const orgTabAtom = searchAtomState.orgTab;

  const [companySearch, setCompanySearchLocal] = useState(orgTabAtom.criteria.companyName);
  const [parentCompanySearch, setParentCompanySearchLocal] = useState(orgTabAtom.criteria.parentCompanyName);

  const { register, handleSubmit, reset } = useForm<FieldValues>({
    defaultValues: orgTabAtom.criteria,
  });

  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage,
  } = usePaginationTable(companies, orgTabAtom.page, orgTabAtom.pageSize);

  // Sync page/pageSize to atom
  useEffect(() => {
    setSearchAtomState((prev) => ({
      ...prev,
      orgTab: { ...prev.orgTab, page, pageSize: rowsPerPage },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const setCompanySearch = (val: string) => {
    setCompanySearchLocal(val);
    setSearchAtomState((prev) => ({
      ...prev,
      orgTab: { ...prev.orgTab, criteria: { ...prev.orgTab.criteria, companyName: val }, hasSearched: true },
    }));
  };

  const setParentCompanySearch = (val: string) => {
    setParentCompanySearchLocal(val);
    setSearchAtomState((prev) => ({
      ...prev,
      orgTab: { ...prev.orgTab, criteria: { ...prev.orgTab.criteria, parentCompanyName: val }, hasSearched: true },
    }));
  };

  const handleResetAtom = () => {
    setSearchAtomState((prev) => ({
      ...prev,
      orgTab: defaultCompanyManagementSearchState.orgTab,
    }));
  };

  const mutationQueryCompanies = useMutation(fetchQueryCompanies, {
    onSuccess: (res) => {
      setCompanies(res.data.data);
      setTotalRecords(res.data.totalRecords);
    },
    onError: () => {},
  });

  useEffect(() => {
    const payload = {
      companyName: companySearch,
      parentCompanyName: parentCompanySearch,
      pageSize: rowsPerPage,
      pageNumber: page,
    };

    mutationQueryCompanies.mutate(payload);
  }, [JSON.stringify(companySearch), JSON.stringify(parentCompanySearch), page, rowsPerPage]);

  return (
    <>
      <OrganizationSearch
        register={register}
        handleSubmit={handleSubmit}
        reset={reset}
        setCompanySearch={setCompanySearch}
        setParentCompanySearch={setParentCompanySearch}
        setPage={setPage}
        onAtomReset={handleResetAtom}
      />
      <OrganizationTable
        page={page}
        totalRecords={totalRecords}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  );
};
